/**
 * Feature flags & Meow Mix toggles — one catalog, grouped by tier.
 *
 * - `FF.FREE` — shipped/free rollout gates
 * - `FF.DEV` — local dev/QA session prefs (not product rollout)
 *
 * (Flip/rotate + zoom/pan are now universal tier capabilities, not flags — see
 * `config/features.php` / `tierCapabilities.ts` — so `FF.PAID` was retired.)
 *
 * Storage: `is_ff_<name>` in localStorage; `?<name>=true` in URL.
 * Client-side only — not a security boundary.
 */

import { getCachedRolesEnabled } from '../services/appConfigService';

/** All toggles grouped by tier. */
export const FF = {
  FREE: {
    HISTORY: 'history',
    AUTH_UI: 'authUi',
    AUTH_DEV_MODAL: 'authDevModal',
  },
  DEV: {
    /** Console snapshots + `imageslicer.dumpSliceDebug()` in the Chop view. */
    EDITOR_DEBUG: 'editorDebug',
  },
} as const;

/** @deprecated use `FF` */
export const FEATURE_FLAGS = FF;

export type FreeFlagName = typeof FF.FREE[keyof typeof FF.FREE];
export type DevFlagName = typeof FF.DEV[keyof typeof FF.DEV];
/** Product feature rollout. */
export type FeatureFlagName = FreeFlagName;
/** All toggles including dev prefs. */
export type RolloutToggleName = FeatureFlagName | DevFlagName;

export function isDevFlag(name: RolloutToggleName): name is DevFlagName {
  return (Object.values(FF.DEV) as string[]).includes(name);
}

const FF_PREFIX = 'is_ff_';

const FF_KEY: Record<RolloutToggleName, string> = {
  [FF.FREE.HISTORY]: 'history',
  [FF.FREE.AUTH_UI]: 'authUi',
  [FF.FREE.AUTH_DEV_MODAL]: 'authDevModal',
  [FF.DEV.EDITOR_DEBUG]: 'editorDebug',
};

function storageKeyForFlag(name: RolloutToggleName): string {
  return `${FF_PREFIX}${FF_KEY[name]}`;
}

export function getFeatureFlagStorageKey(name: RolloutToggleName): string {
  return storageKeyForFlag(name);
}

function readQueryFlag(paramName: string): boolean {
  try {
    const sp = new URLSearchParams(window.location.search);
    return sp.get(paramName) === 'true';
  } catch (_e) {
    return false;
  }
}

function readLocalStorageFlag(name: RolloutToggleName): boolean {
  try {
    const raw = window.localStorage.getItem(storageKeyForFlag(name));
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch (_e) {
    // ignore
  }
  return false;
}

export function getStoredFeatureFlagValue(name: RolloutToggleName): boolean {
  return readLocalStorageFlag(name);
}

function writeLocalStorageFlag(name: RolloutToggleName, value: boolean): void {
  try {
    window.localStorage.setItem(storageKeyForFlag(name), value ? 'true' : 'false');
  } catch (_e) {
    // ignore
  }
}

export function setStoredFeatureFlagValue(name: RolloutToggleName, value: boolean): void {
  writeLocalStorageFlag(name, value);
}

export function isFeatureEnabled(name: RolloutToggleName): boolean {
  const paramName = FF_KEY[name] || name;
  if (readQueryFlag(paramName)) return true;
  return readLocalStorageFlag(name);
}

/**
 * The `?history=true` rollout flag — NON-PRODUCTION ONLY.
 *
 * ⚠️ This is OR'd with the tier gate in EditorPage.tsx, so wherever it is honoured it GRANTS A PAID
 * CAPABILITY to anyone who sets a localStorage key. That was harmless while History was being
 * rolled out to everyone; it stopped being harmless once History became a tier capability
 * (Free and up) with a depth attached to it, because the flag hands out both.
 *
 * Kept for local dev and the test suite, where signing in is not always practical, and closed
 * in the production bundle. Same shape as the server's `dev_bypass`, which is likewise
 * environment-gated rather than removed.
 *
 * Read at CALL time, not captured at module load, so a test can flip the environment.
 */
export function isHistoryEnabled(): boolean {
  if (process.env.NODE_ENV === 'production') return false;
  return isFeatureEnabled(FF.FREE.HISTORY);
}

/**
 * Server master switch for the accounts/tiers system (cached from GET /api/config).
 * When on, the whole app treats auth as live; the per-session `authUi` flag is a local
 * preview override layered on top.
 */
export function isRolesEnabled(): boolean {
  return getCachedRolesEnabled();
}

export function isAuthUiEnabled(): boolean {
  return isRolesEnabled() || isFeatureEnabled(FF.FREE.AUTH_UI);
}

export function isAuthDevModalEnabled(): boolean {
  return isFeatureEnabled(FF.FREE.AUTH_DEV_MODAL);
}

export function isEditorDebugEnabled(): boolean {
  return isFeatureEnabled(FF.DEV.EDITOR_DEBUG);
}

export function isLaravelChopEnabled(): boolean {
  return true;
}

export function installHistoryFlagConsoleHelpers(): void {
  try {
    const w = window as any;
    w.imageslicer = w.imageslicer || {};
    w.imageslicer.enableHistory = () => {
      writeLocalStorageFlag(FF.FREE.HISTORY, true);
      window.location.reload();
    };
    w.imageslicer.disableHistory = () => {
      writeLocalStorageFlag(FF.FREE.HISTORY, false);
      window.location.reload();
    };
    w.imageslicer.setFlag = (name: FeatureFlagName, value: boolean) => {
      writeLocalStorageFlag(name, Boolean(value));
      window.location.reload();
    };
    w.imageslicer.getFlag = (name: FeatureFlagName) => getStoredFeatureFlagValue(name);

    w.imageslicer.enableAuthUi = () => {
      writeLocalStorageFlag(FF.FREE.AUTH_UI, true);
      window.location.reload();
    };
    w.imageslicer.disableAuthUi = () => {
      writeLocalStorageFlag(FF.FREE.AUTH_UI, false);
      window.location.reload();
    };
    w.imageslicer.enableAuthDevModal = () => {
      writeLocalStorageFlag(FF.FREE.AUTH_DEV_MODAL, true);
      window.location.reload();
    };
    w.imageslicer.disableAuthDevModal = () => {
      writeLocalStorageFlag(FF.FREE.AUTH_DEV_MODAL, false);
      window.location.reload();
    };
  } catch (_e) {
    // ignore
  }
}
